import React, { useState } from 'react';
import { Input } from 'reactstrap';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { resetPassword } from 'aws-amplify/auth';
import { useForm } from 'react-hook-form';
import _ from 'lodash';
import { store } from 'redux/store';
import PasswordChange from './PasswordChange';
import StyledReactButton from 'components/Buttons/StyledButton';
import { idPattern } from 'constants/validators';
import { authActions } from 'redux/auth';
import ErrorMessage from './ErrorMessage';
import { AuthError } from '../models';
import VieurekaLogo from 'assets/images/vieureka_vertical@2x.png';

const ForgetPassword: React.FC = () => {
  const { t } = useTranslation(['forgotPassword', 'formError']);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onSubmit',
  });

  const [id, setId] = useState('');
  const [errorInfo, setErrorInfo] = useState<{
    name?: string;
    message?: string;
  }>({});
  const [isVisiblePasswordChange, setIsVisiblePasswordChange] = useState(false);

  const onChange = (value: string): void => {
    setValue('id', value);
    setId(value);
  };

  const onClickBack = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    e.preventDefault();
    store.dispatch(authActions.forgetPassword(false));
  };

  const onClickSubmit = (): void => {
    setErrorInfo({});
    resetPassword({ username: id })
      .then(() => {
        setIsVisiblePasswordChange(true);
      })
      .catch(error => {
        const errorName = error instanceof Error ? error.name : '';
        let errorMessage = error instanceof Error ? error.message : '';
        switch (errorName) {
          case AuthError.codeDeliveryFailureException:
            errorMessage = t('userNotFoundException');
            break;
          case AuthError.userNotFoundException:
            errorMessage = t('userNotFoundException');
            break;
          case AuthError.limitExceededException:
            errorMessage = t('cognitoLimitExceeded');
            break;
          default:
            errorMessage = t('internalErrorException');
            break;
        }
        setErrorInfo({
          name: errorName,
          message: errorMessage,
        });
      });
  };

  const { message: errorMessage } = errorInfo;
  return (
    <Container>
      <StyledImage src={VieurekaLogo} />
      {isVisiblePasswordChange ? (
        <PasswordChange id={id} onClickBack={onClickBack} />
      ) : (
        <StyledForm onSubmit={handleSubmit(onClickSubmit)}>
          <StyledTitle>{t('forgotPassword:sendCodeTitle')}</StyledTitle>
          <StyledInput
            {...register('id', {
              required: true,
              pattern: idPattern,
            })}
            value={id}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange(e.target.value)
            }
            placeholder={t('forgotPassword:id')}
          />
          <StyledSubmitButton>
            <StyledReactButton color={'primary'} block>
              {t('forgotPassword:sendCode')}
            </StyledReactButton>
          </StyledSubmitButton>
          {(!_.isEmpty(errors) || errorMessage !== null) && (
            <ErrorMessage errors={errors} errorMessage={errorMessage} />
          )}
          <div>
            <StyledAnchor href={'#'} onClick={onClickBack}>
              {t('forgotPassword:back')}
            </StyledAnchor>
          </div>
        </StyledForm>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 95vh;
`;

const StyledForm = styled.form`
  width: 330px;
  text-align: -webkit-center;
`;

const StyledTitle = styled.p`
  margin-bottom: 15px;
  font-size: 2rem;
`;

const StyledInput = styled(Input)`
  width: 280px;
  margin-bottom: 8px;
`;

const StyledSubmitButton = styled.div`
  width: 280px;
  margin-bottom: 20px;
`;

const StyledAnchor = styled.a`
  :hover {
    text-decoration: underline;
  }
`;

const StyledImage = styled.img`
  width: 150px;
  margin-bottom: 30px;
`;

export default ForgetPassword;
